import {
	TAG_TYPES,
	type TContractType,
	type TDate,
	type TFeedbackPayload,
	type TFieldOfStudyView,
	type TPagination,
	type TSubscriptionStatus,
	type TTag,
	type TTranslation,
	type TValidatePayload,
} from "@/assets/js/globalTypes.js";
import { axios } from "./config.js";
import type {
	TAuthVerifyCreate,
	TAuthVerifyResend,
	TAxiosGenRes,
	TBasicType,
	TCreateSchoolParams,
	TForgotPass,
	TLoginParam,
	TLoginRes,
	TRegisterParams,
	TResetPass,
	TSchoolSearchParams,
	TTeacherSearchParams,
	TTeacherClassRequestsParams,
	TCalendarFiltersParams,
	TCvExperienceEdit,
	TSchoolAdminSearch,
	TEditSchoolLocation,
	ICalendarClasessParams,
	ITeacherAvailabilityParams,
	ISetTeacherAvailability,
	TAddClassRequestParams,
	TSingleClass,
} from "./types";

function getTags(
	params: {
		tag_type: TTag["tag_type"];
		tag_name?: TTag["tag_name"];
	} & Partial<TPagination>,
) {
	return axios.get("tags/tags-get", {
		params,
	});
}
export default {
	//#region base
	getSettings: () => axios.get("status"),
	getCities: (params: { cit_name?: string; limit?: number }) =>
		axios.get("cities/cities-get", { params }),
	getContractTypes: () => axios.get("contracts/contract-types-get"),
	getFileTypes: (ctt_type?: TContractType[]) => {
		const contract_types = ctt_type
			? ctt_type.map((el) => el.ctt_code).join(",")
			: undefined;
		return axios.get("files/file-types-get", { params: { contract_types } });
	},
	downloadFile: (fld_id: string) =>
		axios.get("files/download/" + fld_id, { responseType: "blob" }),
	downloadFileRaw: (url: string) => axios.get(url, { responseType: "blob" }),
	removeFile: (fil_id: number) => axios.post("files/files-remove/" + fil_id),
	addFile: (params: FormData) =>
		axios.post("files/files-add", params, {
			headers: { "Content-Type": "multipart/form-data" },
		}),
	getNotifications: (params?: {
		page?: number;
		per_page?: number;
		mark_as_seen?: boolean;
	}) => axios.get("notifications/notifications-get", { params }),
	markSeenNotifications: (params: { ntf_ids: number[] }) =>
		axios.post("notifications/notifications-mark-seen", params),
	removeNotification: (params: { ntf_id: number }) =>
		axios.post("notifications/notifications-remove/" + params.ntf_id),
	//#endregion
	//#region auth
	authLogin: (params: TLoginParam) =>
		axios.post<TLoginRes>("auth/login", params),
	authRegister: (params: TRegisterParams) =>
		axios.post<TAxiosGenRes<TBasicType>>("auth/register", params),
	authRenewSession: () => {
		let timezone_location = "";
		let timezone_minutes = 0;
		try {
			timezone_minutes = -1 * new Date().getTimezoneOffset();
			timezone_location = Intl.DateTimeFormat().resolvedOptions().timeZone;
		} catch {}
		return axios.get<{ data: { token: string } }>("auth/check-jwt", {
			params: { timezone_minutes, timezone_location },
		});
	},
	authLogout: () => axios.post("auth/logout", { validateStatus: null }),
	authVerifyCreate: (params: TAuthVerifyCreate) =>
		axios.post<TLoginRes>("auth/verify-email", params),
	authVerifyResend: (params: TAuthVerifyResend) =>
		axios.put("auth/verify", params),
	authForgot: (params: TForgotPass) =>
		axios.get("auth/reset-password", { params }),
	authResetPass: (params: TResetPass) =>
		axios.post("auth/reset-password", params),
	getTeachingLanguages: () => axios.get("languages/teaching-languages-get"),
	editUser: (params: any) => axios.post("users/profile-edit", params),
	impersonateSchoolAdmin: (usr_id: number) =>
		axios.post("auth/preview", { usr_id }),
	//endregion

	//region schools
	addSchool: (params: TCreateSchoolParams) =>
		axios.post("schools/school-add", params),
	editSchool: (scl_id: number, params: FormData) =>
		axios.post("schools/school-edit/" + scl_id, params),
	getSchools: (params: TSchoolSearchParams) =>
		axios.get("schools/school-get", { params }),
	getSchool: (scl_id: number) => axios.get("schools/school-get/" + scl_id),
	getSchoolPublic: (scl_id: number) =>
		axios.get("users/teachers/school-get/" + scl_id),
	deleteSchool: (scl_id: number) =>
		axios.post("schools/school-remove/" + scl_id),
	getSchoolInfo: (slc_id: number) => axios.get("schools/school-get/" + slc_id),
	removeSchoolAdmin: (params: { usr_id: number }) =>
		axios.post("users/admins/admin-remove/" + params.usr_id),
	getSchoolAdmins: (params: TSchoolAdminSearch) =>
		axios.post("schools/admin-get", params),
	addSchoolAdmin: (params: {
		scl_id: number;
		usr_email: string;
		usr_fullname: string;
		usr_schoolrole: string;
	}) => axios.post("users/admins/admin-add", params),
	editSchoolAdmin: (params: {
		usr_id: number;
		usr_email?: string;
		usr_fullname?: string;
		usr_schoolrole?: string;
	}) => axios.post("users/profile-edit", params),
	addSchoolLocation: (params: {
		scl_id: number;
		cit_id: number;
		slc_address: string;
		slc_name: string;
	}) => axios.post("schools/locations/location-add/" + params.scl_id, params),
	editSchoolLocation: (params: TEditSchoolLocation) =>
		axios.post("schools/locations/location-edit/" + params.slc_id, params),
	removeSchoolLocation: (params: { scl_id?: number; slc_id: number }) =>
		axios.post("schools/locations/location-remove/" + params.slc_id, {
			params,
		}),
	getFieldsOfStudy: (params?: { sfp_name: string }) =>
		axios.get("subjects/subjects-get", { params }),
	getFieldsOfStudySingle: (sfp_id: number) =>
		axios.get("subjects/subjects-get/" + sfp_id),
	postFieldOfStudy: (params: {
		sfp_name: string;
		translations?: TTranslation<Pick<TFieldOfStudyView, "sfp_name">>;
		sfp_id_parent: number;
	}) => axios.post("subjects/subjects-add", params),
	editFieldOStudy: (params: {
		sfp_id: number;
		sfp_name: string;
		translations?: TTranslation<Pick<TFieldOfStudyView, "sfp_name">>;
	}) => axios.post("subjects/subjects-edit/" + params.sfp_id, params),
	deleteFieldOfStudy: (sfp_id: number) =>
		axios.post("subjects/subjects-remove/" + sfp_id),
	getTags,
	addTag: (
		params: Omit<TTag, "tag_id"> & {
			translations?: TTranslation<Pick<TTag, "tag_name">>;
		},
	) => axios.post("tags/tags-add", params),
	deleteTag: (params: Pick<TTag, "tag_id" | "tag_type">) =>
		axios.post("tags/tags-remove/" + params.tag_id, params),
	editTag: (
		params: TTag & { translations?: TTranslation<Pick<TTag, "tag_name">> },
	) => axios.post("tags/tags-edit/" + params.tag_id, params),
	getTag: (params: { tag_id: TTag["tag_id"]; tag_type: TTag["tag_type"] }) =>
		axios.get("tags/tags-get/" + params.tag_id, { params }),
	getAllNatures: (params: { tag_name?: string } & Partial<TPagination>) =>
		getTags({
			tag_type: TAG_TYPES.SCHOOL_TYPE,
			...params,
		}),
	getAllSkills: (
		params: {
			tag_name?: string;
			ignore_pagination?: "" | "1";
		} & Partial<TPagination>,
	) =>
		getTags({
			tag_type: TAG_TYPES.SKILLS,
			...params,
		}),
	getAllPrograms: (
		params: {
			tag_name?: string;
			scl_id?: number;
			ignore_pagination?: boolean;
		} & Partial<TPagination>,
	) =>
		getTags({
			tag_type: TAG_TYPES.PROGRAMS,
			...params,
		}),

	getSubscription: (scl_id: number) =>
		axios.get("subscriptions/subscriptions-get/" + scl_id),
	addSubscription: (params: {
		sbr_end: TDate;
		sbr_start: TDate;
		scl_id: number;
		sbr_price: number;
	}) => axios.post("subscriptions/subscriptions-add/" + params.scl_id, params),
	editSubscription: (params: {
		sbr_id: number;
		sbr_end: string;
		sbr_start: string;
		sbr_price: number;
		sbr_status: TSubscriptionStatus;
	}) => axios.post("subscriptions/subscriptions-edit/" + params.sbr_id, params),
	deleteSubscription: (sbr_id: number) =>
		axios.post("subscriptions/subscriptions-remove/" + sbr_id),
	//endregion

	//region teachers
	getTeachers: (params: TTeacherSearchParams & Partial<TPagination>) =>
		axios.get("users/teachers/teachers-get", { params }),
	getTeacher: (usr_id: number) =>
		axios.get("users/teachers/teachers-get/" + usr_id),
	getPendingTeachers: (params: TTeacherSearchParams) =>
		axios.get("validation/teachers/validation-get", { params }),
	getClassRequests: (params: TTeacherClassRequestsParams) =>
		axios.get("classes/teachers/requests-get", { params }),
	getTeacherClassRequest: (crq_id: number) =>
		axios.get("classes/teachers/requests-get/" + crq_id),
	getTeacherProfile: (usr_id: number) =>
		axios.get("validation/teachers/validation-get/" + usr_id),
	getTeacherFile: (url: string) => axios.get(url, { responseType: "blob" }),
	validateTeacher: (params: TValidatePayload) =>
		axios.post("validation/teachers/validation-approve", params),
	denyValidation: (params: TFeedbackPayload) =>
		axios.post("validation/teachers/validation-reject", params),
	blacklistTeacher: (usr_id: number) =>
		axios.post("users/teachers/profile-ban/" + usr_id),
	finishProfile: (params: FormData) =>
		axios.post("users/teachers/profile-complete", params),
	getTeacherDashboard: () => axios.get("users/teachers/dashboard-get"),
	getCalendarClasses: (teacher_id: number, params: ICalendarClasessParams) => {
		const newParams: any = { ...params };
		newParams.scl_ids = params.scl_ids?.join(",");
		newParams.crq_titles = params.crq_titles?.join(",");

		return axios.get("classes/teachers/" + teacher_id, { params: newParams });
	},
	getTeacherAvailability: (
		teacher_id: number,
		params: ITeacherAvailabilityParams,
	) => axios.get("availabilities/availability-get/" + teacher_id, { params }),
	postTeacherAvailability: (params: ISetTeacherAvailability) =>
		axios.post("availabilities/availability-add", params),
	patchTeacherAvailability: (params: any) =>
		axios.post("availabilities/availability-edit/" + params.avl_id, {
			avl_start: params.avl_start,
			avl_end: params.avl_end,
		}),
	deleteTeacherAvailability: (avlID: number) =>
		axios.post("availabilities/availability-remove/" + avlID),
	deleteTeacherAvailabilities: (params: { avl_ids: number[] }) =>
		axios.post("availabilities/availability-remove", params),

	//endregion

	//#region experiences
	addExperiences: (params: { experiences: TCvExperienceEdit["new"] }) =>
		axios.post("experiences/experience-add", params),
	editExperiences: (params: { experiences: TCvExperienceEdit["edited"] }) =>
		axios.post("experiences/experience-edit", params),
	removeExperiences: (params: { cve_ids: TCvExperienceEdit["removed"] }) =>
		axios.post("experiences/experience-remove", params),
	//#endregion

	//region calendar
	getCalendarFilters: (params: TCalendarFiltersParams) =>
		axios.get("classes/filters-get", { params }),
	getSchoolEvents: (sclId: number, params: any) =>
		axios.get("classes/schools/" + sclId, { params }),
	getSchoolClassess: (scl_id: number, params: ICalendarClasessParams) =>
		axios.get("classes/schools/" + scl_id, { params }),
	//endregion

	//#region classrequests (School Admin)
	getSchoolClassRequest: (crq_id: number) =>
		axios.get("classes/schools/requests-get/" + crq_id),
	addClassRequest: (params: TAddClassRequestParams) =>
		axios.post("classes/classes-add", params),
	editClassRequest: (params: TAddClassRequestParams) =>
		axios.post("classes/classes-edit/" + params.crq_id, params),
	cancelClass: (params: { id: number; clr_id: number }) =>
		axios.post("classes/classes-cancel/" + params.id, params),
	getCancelationReasons: () => axios.get("classes/cancelation-reasons"),
	cancelCourse: (params: { crq_id: number; clr_id: number }) =>
		axios.post("classes/classes-cancel", params),

	getSchoolClassRequests: (params: any) =>
		axios.get("classes/schools/requests-get", { params }),
	getTeachersForClass: (
		params: {
			usr_fullname: string;
			cities?: string | number[];
			availabilities: { start: string; end: string }[];
			subjects: number[];
			status: "Available" | "Other";
			rate_from?: number;
			rate_to?: number;
			usr_experience?: string;
			contract_types?: string[];
			lang?: string;
			skills?: number[];
			usr_remotework?: number;
			usr_professionalexperience?: string;
		} & Partial<TPagination>,
	) => axios.post("classes/teachers/teachers-for-class", params),
	acceptClassRequest: (crq_id: number) =>
		axios.post("classes/teachers/accept", { crq_id }),
	rejectClassRequest: (params: { crq_id: number; clr_reason?: string }) =>
		axios.post("classes/teachers/reject", params),
	uploadClassRequestFiles: (params: FormData) =>
		axios.post("classes/attach-syllabus", params, {
			headers: { "Content-Type": "multipart/form-data" },
		}),
	deleteClassRequestFiles: (params: { crq_id: number }) =>
		axios.post("classes/remove-syllabus", params),
	checkBookInfo: (params: {
		scl_id: number;
		classes: Pick<TSingleClass, "cls_start" | "cls_end" | "slc_id">[];
	}) => axios.post("classes/teachers/check-book-info", params),
};
